// ============================================================================
// Schema + validação do payload `dados` do contrato (§6 do brief).
// Espelha o `Code24` do workflow n8n [RU] Criar Contrato e a skill
// /criar-contrato. Esta MESMA lógica é replicada na Edge Function contract-intake
// (Deno) — mantenha as duas em sincronia.
// ============================================================================

import { z } from "zod";
import type { Plano } from "./types";

/** As 23 chaves canônicas do payload de contrato. */
export interface DadosContrato {
  nome_empresarial?: string; // só PJ (razão social)
  cnpj?: string; // PJ, 14 díg
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string; // UF
  complemento?: string;
  cep?: string;
  nome_completo?: string; // responsável
  profissao?: string; // PF
  estado_civil?: string; // PF
  nacionalidade?: string; // PF, default Brasileira
  rg?: string; // PF
  cpf?: string; // responsável, 11 díg
  email_assinatura?: string;
  dia_pagamento?: number | string; // 1-31
  plano?: Plano | string;
  valor_mensal?: number;
  volume_posts?: string; // se Social, senão "-"
  telefone?: string;
  tempo_contrato?: number;
  data_inicio?: string; // DD/MM/YYYY
  nome_projeto?: string;
  transcricoes?: string[];
  modelo_contrato?: "PF" | "PJ";
}

// ----------------------------------------------------------------------------
// Normalização (aplicar ANTES de validar)
// ----------------------------------------------------------------------------

export const soDigitos = (v: unknown): string => String(v ?? "").replace(/\D/g, "");

/** Telefone: só dígitos; remove prefixo 55 se vier com 12+ díg começando em 55. */
export function normalizarTelefone(v: unknown): string {
  let d = soDigitos(v);
  if (d.length >= 12 && d.startsWith("55")) d = d.slice(2);
  return d;
}

const INVISIVEIS = /[\u200B-\u200D\uFEFF]/g;

/** Texto livre: tira caractere invisível e espaço duplicado. MANTÉM acento. */
export function normalizarTexto(v: unknown): string {
  return String(v ?? "").replace(INVISIVEIS, "").replace(/\s+/g, " ").trim();
}

/**
 * E-mail: sem espaço, sem acento, sem caractere fora do ASCII, tudo minúsculo.
 * O D4Sign DESCARTA em silêncio (e ainda responde 200) o signatário cujo e-mail
 * sai do ASCII — foi o que deixou um contrato sem o cliente na lista de
 * assinatura. Normalizar aqui evita depender do usuário digitar "limpo".
 */
export function normalizarEmail(v: unknown): string {
  let s = String(v ?? "").replace(INVISIVEIS, "").trim();
  s = s.replace(/^mailto:/i, "").replace(/^</, "").replace(/>$/, "");
  s = s.replace(/\s+/g, "");
  s = s.normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // á → a, ç → c
  s = s.replace(/[^\x20-\x7E]/g, "");                     // descarta o resto não-ASCII
  return s.toLowerCase();
}

/** "R$ 2.000,00" | "2.000" | 2000 → 2000. undefined quando não vira número. */
export function parseValorBR(v: unknown): number | undefined {
  if (v === null || v === undefined || v === "") return undefined;
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  let s = String(v).replace(INVISIVEIS, "").trim().replace(/^r\$\s*/i, "").replace(/\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** Link de Google Docs — e o unico formato de transcricao que o n8n consegue ler. */
const RE_GOOGLE_DOC = /https?:\/\/(?:www\.)?docs\.google\.com\/document\/(?:u\/\d+\/)?d\/([A-Za-z0-9_-]{20,})/gi;

/** Teto de transcricoes por contrato. */
const MAX_TRANSCRICOES = 5;

/**
 * Aceita string (uma ou varias URLs, em qualquer separador) ou array e devolve
 * links canonicos de Google Docs, sem repeticao. O que nao for doc do Google e
 * descartado: e ele que o workflow do briefing consegue abrir.
 */
export function normalizarTranscricoes(v: unknown): string[] {
  const bruto = Array.isArray(v) ? v.map((x) => String(x ?? "")).join("\n") : String(v ?? "");
  const texto = bruto.replace(INVISIVEIS, "");
  const links: string[] = [];
  RE_GOOGLE_DOC.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RE_GOOGLE_DOC.exec(texto)) !== null) {
    const url = `https://docs.google.com/document/d/${m[1]}/edit`;
    if (!links.includes(url)) links.push(url);
    if (links.length >= MAX_TRANSCRICOES) break;
  }
  return links;
}

const CAMPOS_TEXTO = [
  "nome_completo", "nome_empresarial", "endereco", "numero", "complemento",
  "bairro", "cidade", "profissao", "estado_civil", "nacionalidade",
  "plano", "volume_posts", "rg", "nome_projeto",
];

/**
 * Normaliza uma cópia dos dados ANTES de validar/disparar: documentos e CEP só
 * com dígitos, e-mail em ASCII minúsculo, texto sem espaço duplicado, valor em
 * número, UF em maiúscula.
 */
export function normalizarDados(input: DadosContrato): DadosContrato {
  const d: DadosContrato = { ...input };
  const livre = d as Record<string, unknown>;
  if (d.cpf != null) d.cpf = soDigitos(d.cpf);
  if (d.cnpj != null) d.cnpj = soDigitos(d.cnpj);
  if (d.cep != null) d.cep = soDigitos(d.cep);
  if (d.telefone != null) d.telefone = normalizarTelefone(d.telefone);
  if (d.email_assinatura != null) d.email_assinatura = normalizarEmail(d.email_assinatura);
  if (d.valor_mensal != null) d.valor_mensal = parseValorBR(d.valor_mensal);
  if (d.transcricoes != null) d.transcricoes = normalizarTranscricoes(d.transcricoes);
  for (const campo of CAMPOS_TEXTO) {
    if (livre[campo] != null) livre[campo] = normalizarTexto(livre[campo]);
  }
  if (d.estado != null) {
    const uf = normalizarTexto(d.estado);
    d.estado = uf.length === 2 ? uf.toUpperCase() : uf;
  }
  if (d.plano != null) d.plano = normalizarPlano(d.plano) ?? normalizarTexto(d.plano);
  // Plano sem Social não tem volume de posts. Fica com o código porque o modelo
  // degenerava repetindo "-" neste campo até estourar a resposta inteira.
  if (d.plano && !/social/i.test(d.plano)) d.volume_posts = "-";
  return d;
}

// ----------------------------------------------------------------------------
// Detecção PF / PJ
// ----------------------------------------------------------------------------

export function detectarModelo(d: DadosContrato): "PF" | "PJ" {
  return soDigitos(d.cnpj).length === 14 ? "PJ" : "PF";
}

// ----------------------------------------------------------------------------
// Parse de data (DD/MM/YYYY ou ISO YYYY-MM-DD)
// ----------------------------------------------------------------------------

export function parseDataInicio(v: unknown): Date | null {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const br = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (br) {
    const [, dd, mm, yyyy] = br;
    const dt = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const dt = new Date(`${iso[1]}-${iso[2].padStart(2, "0")}-${iso[3].padStart(2, "0")}T00:00:00`);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  return null;
}

// ASCII estrito: e-mail que fuja disto é descartado sem aviso pelo D4Sign.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// ----------------------------------------------------------------------------
// Validação no estilo Code24 — retorna a PRIMEIRA pendência encontrada.
// ----------------------------------------------------------------------------

export interface ValidacaoOk {
  ok: true;
  dados: DadosContrato;
  modelo: "PF" | "PJ";
}
export interface ValidacaoErro {
  ok: false;
  missing: string;
  message: string;
}
export type Validacao = ValidacaoOk | ValidacaoErro;

const falta = (campo: string, label: string): ValidacaoErro => ({
  ok: false,
  missing: campo,
  message: `Antes de gerar o contrato, preencha o campo "${label}".`,
});

/**
 * Valida `dados` na ordem exata do Code24. Normaliza internamente.
 * A primeira checagem que falhar retorna { ok:false, missing, message }.
 */
export function validarDados(input: DadosContrato): Validacao {
  const d = normalizarDados(input);

  // 1. tempo_contrato ≥ 1
  const tempo = Number(String(d.tempo_contrato ?? "").match(/\d+/)?.[0] ?? "");
  if (!tempo || tempo < 1) return falta("tempo_contrato", "Tempo de contrato (meses)");

  // 2. data_inicio parseável
  if (!parseDataInicio(d.data_inicio)) return falta("data_inicio", "Data de início (DD/MM/AAAA)");

  // 3. modelo
  const modelo = detectarModelo(d);

  // 4. documento
  if (modelo === "PJ" && d.cnpj!.length !== 14) return falta("cnpj", "CNPJ (14 dígitos)");
  if (modelo === "PF" && soDigitos(d.cpf).length !== 11) return falta("cpf", "CPF (11 dígitos)");

  // 5. ao menos um nome
  if (!d.nome_completo && !d.nome_empresarial) return falta("nome_completo", "Nome do responsável");

  // 6. PJ exige razão social
  if (modelo === "PJ" && !d.nome_empresarial) return falta("nome_empresarial", "Razão social (nome empresarial)");

  // 7. nome do responsável sempre
  if (!d.nome_completo) return falta("nome_completo", "Nome do responsável");

  // 8. email de assinatura (com @ e domínio)
  if (!d.email_assinatura || !EMAIL_RE.test(d.email_assinatura)) {
    return falta("email_assinatura", "E-mail de assinatura (D4Sign) — só letras sem acento, no formato nome@dominio.com");
  }

  // 9. plano
  if (!d.plano) return falta("plano", "Plano");

  // 10. valor mensal
  if (d.valor_mensal == null || Number(d.valor_mensal) <= 0) return falta("valor_mensal", "Valor mensal");

  // 11. dia de pagamento (1-31)
  const dia = Number(String(d.dia_pagamento ?? "").match(/\d+/)?.[0] ?? "");
  if (!dia || dia < 1 || dia > 31) return falta("dia_pagamento", "Dia de pagamento (1-31)");

  // 12. cpf do responsável sempre
  if (soDigitos(d.cpf).length !== 11) return falta("cpf", "CPF do responsável (11 dígitos)");

  // 13. endereço mínimo
  if (!d.endereco) return falta("endereco", "Endereço (logradouro)");
  if (!d.cidade) return falta("cidade", "Cidade");
  if (!d.estado) return falta("estado", "Estado (UF)");
  if (!soDigitos(d.cep)) return falta("cep", "CEP");

  // 14. nome oficial do projeto — nomeia ClickUp, Discord, grupo e pasta do Drive
  if (!nomeProjetoValido(d.nome_projeto)) {
    return falta("nome_projeto", "Nome oficial do projeto (ex.: Dra. Tayse Oliveira — nome de pessoa ou marca, nunca CNPJ, CPF ou número)");
  }

  // 15. transcrição da reunião de fechamento — vira o briefing do time
  if (!d.transcricoes || d.transcricoes.length === 0) {
    return falta("transcricoes", "Link do Google Docs da transcrição da reunião de fechamento");
  }

  return { ok: true, dados: { ...d, modelo_contrato: modelo, tempo_contrato: tempo, dia_pagamento: dia }, modelo };
}

// ----------------------------------------------------------------------------
// Tabela de preços de referência (só ALERTA divergência — nunca bloqueia).
// ----------------------------------------------------------------------------

export const PRECO_REFERENCIA: Record<string, number> = {
  "Light Performance": 2500,
  "Full Performance": 3500,
  "Light Performance + Social": 4500,
  "Full Performance + Social": 6000,
};

/** Retorna mensagem de alerta se o valor divergir da tabela, senão null. */
export function alertaPreco(plano: string | undefined, valor: number | undefined): string | null {
  if (!plano || valor == null) return null;
  const ref = PRECO_REFERENCIA[plano];
  if (ref == null) return null;
  if (Number(valor) !== ref) {
    return `Valor R$ ${Number(valor).toLocaleString("pt-BR")} difere da tabela (${plano} = R$ ${ref.toLocaleString("pt-BR")}). Desconto/condição especial? Seguindo com o valor informado.`;
  }
  return null;
}

// ----------------------------------------------------------------------------
// Schema zod do formulário de handoff (validação client-side simples).
// ----------------------------------------------------------------------------

export const handoffFormSchema = z.object({
  nome_cliente: z.string().min(2, "Informe o nome do cliente"),
  link_reuniao: z.string().url("Link inválido").or(z.literal("")).optional(),
  plano_acordado: z.string().optional(),
  valor_acordado: z.coerce.number().nonnegative().optional(),
  termos_notas: z.string().optional(),
});

export type HandoffForm = z.infer<typeof handoffFormSchema>;

// ----------------------------------------------------------------------------
// Extração determinística a partir da fala do usuário. O modelo anota mal (lê
// um bloco com 15 campos e registra 3) e, se recusa um dado uma vez, imita o
// próprio histórico e repete a recusa. Aqui o dado entra por código: quem cola
// a ficha inteira de uma vez não é interrogado campo a campo.
// ----------------------------------------------------------------------------

const RE_EMAIL_SOLTO = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;

/** Nº de dígitos exigido por campo, quando o usuário responde "solto". */
const DIGITOS_POR_CAMPO: Record<string, number> = { cpf: 11, cnpj: 14, cep: 8 };

/**
 * O nome do projeto e o unico campo que vira NOME PUBLICO: ele batiza a task do
 * ClickUp, o canal do Discord, o grupo de WhatsApp do cliente e a pasta do Drive.
 * Ja aconteceu de um CNPJ colado na primeira pergunta do chat virar o nome do
 * grupo, entao aqui nada que pareca documento/numero/link passa: fica faltando e
 * o sistema pergunta de novo.
 */
export function nomeProjetoValido(v: unknown): boolean {
  const t = normalizarTexto(v);
  if (t.length < 2 || t.length > 120) return false;
  const letras = (t.match(/[A-Za-zÀ-ÖØ-öø-ÿ]/g) ?? []).length;
  if (letras < 2) return false;                       // "27.062.681/0001-79", "12345"
  if (/^[\d\s.\-/()]+$/.test(t)) return false;         // so digito e pontuacao de documento
  if (RE_EMAIL_SOLTO.test(t)) return false;           // e-mail nao e nome de projeto
  if (/https?:\/\/|www\.|docs\.google\.com/i.test(t)) return false; // link colado
  // "CNPJ 27.062.681/0001-79" tem letra, mas e documento: se a maior parte do
  // que sobra for digito, nao e nome. "Studio 21" passa (2 digitos so).
  const dig = soDigitos(t);
  const semEspaco = t.replace(/\s/g, "").length || 1;
  if (dig.length >= 8 && dig.length / semEspaco > 0.5) return false;
  return true;
}

/** Todos os blocos de dígitos da mensagem, já limpos (para resposta "solta"). */
function blocosDeDigitos(texto: string): string[] {
  return (texto.match(/[\d][\d.\-/\s]*/g) ?? []).map((b) => soDigitos(b)).filter(Boolean);
}

/** Só para DESAMBIGUAR resposta solta: "83999998888" (telefone) tem os mesmos
 *  11 dígitos de um CPF. Não é usado na validação final — CPF rotulado pelo
 *  usuário ("CPF: ...") ou pontuado é aceito como ele afirmou. */
function cpfTemDvValido(cpf: string): boolean {
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const dv = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i++) soma += Number(cpf[i]) * (ate + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(9) === Number(cpf[9]) && dv(10) === Number(cpf[10]);
}

/**
 * Formato pontuado canônico — inequívoco, então vale mesmo que o sistema tenha
 * perguntado outra coisa. Sem pontuação NÃO se aceita fora do campo perguntado:
 * "83999998888" (telefone) tem os mesmos 11 dígitos de um CPF.
 */
function acharFormatado(texto: string): DadosContrato {
  const achado: DadosContrato = {};
  const cpf = texto.match(/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/)?.[0];
  if (cpf) achado.cpf = soDigitos(cpf);
  const cnpj = texto.match(/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/)?.[0];
  if (cnpj) achado.cnpj = soDigitos(cnpj);
  const cep = texto.match(/\b\d{5}-\d{3}\b/)?.[0];
  if (cep) achado.cep = soDigitos(cep);
  return achado;
}

// Ordem IMPORTA: o mais específico primeiro ("estado civil" antes de "estado",
// "nome empresarial" antes de "nome"), senão o rótulo genérico rouba o match.
const ROTULOS: { campo: string; padrao: string }[] = [
  { campo: "nome_empresarial", padrao: "nome empresarial|raz[ãa]o social|nome da empresa" },
  { campo: "nome_projeto", padrao: "nome oficial do projeto|nome do projeto|nome oficial|projeto" },
  { campo: "cnpj", padrao: "cnpj|cpnj" },
  { campo: "estado_civil", padrao: "estado civil" },
  { campo: "nome_completo", padrao: "nome completo|nome do respons[áa]vel|respons[áa]vel|nome" },
  { campo: "cpf", padrao: "cpf" },
  { campo: "rg", padrao: "rg|identidade" },
  { campo: "profissao", padrao: "profiss[ãa]o|cargo" },
  { campo: "nacionalidade", padrao: "nacionalidade" },
  { campo: "email_assinatura", padrao: "e-?mail[^:\\n]{0,30}?|email" },
  { campo: "telefone", padrao: "telefone|whatsapp|celular" },
  { campo: "data_inicio", padrao: "data de in[íi]cio|data in[íi]cio|in[íi]cio" },
  { campo: "tempo_contrato", padrao: "tempo de contrato|vig[êe]ncia|dura[çc][ãa]o" },
  { campo: "dia_pagamento", padrao: "(?:melhor )?dia (?:de |do )?pagamento|dia de vencimento|vencimento" },
  { campo: "valor_mensal", padrao: "valor mensal|fixo mensal|mensalidade|investimento|valor" },
  { campo: "volume_posts", padrao: "volume de posts|posts por semana|volume_posts" },
  { campo: "plano", padrao: "plano" },
  { campo: "transcricoes", padrao: "transcri[çc][ãa]o|transcri[çc][õo]es|links? da(?:s)? transcri[çc][ãa]o|links? da reuni[ãa]o|doc da reuni[ãa]o|ata da reuni[ãa]o|grava[çc][ãa]o da reuni[ãa]o" },
  { campo: "endereco", padrao: "endere[çc]o|logradouro|rua" },
  { campo: "numero", padrao: "n[úu]mero|n[º°]" },
  { campo: "complemento", padrao: "complemento" },
  { campo: "bairro", padrao: "bairro" },
  { campo: "cidade", padrao: "cidade|munic[íi]pio" },
  { campo: "estado", padrao: "estado|uf" },
  { campo: "cep", padrao: "cep" },
];

const RE_ROTULO = new RegExp(
  `(?:^|[\\n\\r]|[-–—*•;|]\\s|\\s{2,})\\s*(${ROTULOS.map((r) => r.padrao).join("|")})[^:\\n]{0,40}?\\s*:\\s*`,
  "gi",
);

function campoDoRotulo(rotulo: string): string | undefined {
  const limpo = rotulo.trim().toLowerCase();
  for (const r of ROTULOS) {
    if (new RegExp(`^(?:${r.padrao})$`, "i").test(limpo)) return r.campo;
  }
  return undefined;
}

/** Planos canônicos: o Switch do n8n compara com igualdade case-sensitive, então
 *  "full performance" não casaria em NENHUMA saída e o contrato morreria calado. */
export function normalizarPlano(v: unknown): string | undefined {
  const s = normalizarTexto(v).toLowerCase();
  if (!s) return undefined;
  const social = /social/.test(s);
  if (/full/.test(s)) return social ? "Full Performance + Social" : "Full Performance";
  if (/light/.test(s)) return social ? "Light Performance + Social" : "Light Performance";
  return undefined;
}

function limparValor(v: string): string {
  return v.replace(/^[\s:_*\-–—]+/, "").replace(/[\s:_*\-–—]+$/, "").trim();
}

/** Converte o texto solto de um rótulo no valor final do campo. undefined = descarta. */
function valorDoCampo(campo: string, bruto: string): unknown {
  const v = limparValor(bruto);
  if (!v) return undefined;
  switch (campo) {
    case "cpf": case "cnpj": case "cep": {
      const dig = soDigitos(v);
      return dig.length === DIGITOS_POR_CAMPO[campo] ? dig : undefined;
    }
    case "telefone": {
      const tel = normalizarTelefone(v);
      return tel.length >= 10 ? tel : undefined;
    }
    case "email_assinatura": {
      const e = normalizarEmail(v.match(RE_EMAIL_SOLTO)?.[0] ?? v);
      return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(e) ? e : undefined;
    }
    case "dia_pagamento": {
      const n = Number(v.match(/\d+/)?.[0] ?? "");
      return n >= 1 && n <= 31 ? n : undefined;
    }
    case "tempo_contrato": {
      const n = Number(v.match(/\d+/)?.[0] ?? "");
      return n >= 1 ? n : undefined;
    }
    case "valor_mensal": return valorSolto(v);
    case "data_inicio": return v.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/)?.[0];
    case "plano": return normalizarPlano(v) ?? normalizarTexto(v);
    case "estado": {
      const uf = normalizarTexto(v);
      return uf.length === 2 ? uf.toUpperCase() : uf;
    }
    case "transcricoes": {
      const links = normalizarTranscricoes(v);
      return links.length ? links : undefined;
    }
    case "nome_projeto": {
      const t = normalizarTexto(v);
      return nomeProjetoValido(t) ? t : undefined;
    }
    case "volume_posts": {
      const t = normalizarTexto(v);
      return t.length <= 40 ? t : undefined;
    }
    default: {
      const t = normalizarTexto(v);
      return t.length <= 200 ? t : t.slice(0, 200);
    }
  }
}

/** "3k" / "3 mil" / "R$ 3.500,00" — como se escreve no dia a dia. */
function valorSolto(texto: string): number | undefined {
  const k = texto.match(/(\d+(?:[.,]\d+)?)\s*(?:k\b|mil\b)/i);
  if (k) return Number(k[1].replace(",", ".")) * 1000;
  const rs = texto.match(/r?\$?\s*\d[\d.,]*/i)?.[0];
  const n = parseValorBR(rs ?? texto);
  return n && n > 0 ? n : undefined;
}

/**
 * Lê pares "rótulo: valor" — é assim que a ficha do cliente chega colada de uma
 * vez. Fatia o texto entre um rótulo e o próximo, então um valor pode conter
 * hífen ("Rua Jorge Street - ") sem se perder.
 */
function extrairRotulados(texto: string): DadosContrato {
  const marcas: { campo: string; iniValor: number; iniMatch: number }[] = [];
  RE_ROTULO.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RE_ROTULO.exec(texto)) !== null) {
    const campo = campoDoRotulo(m[1]);
    if (campo) marcas.push({ campo, iniValor: RE_ROTULO.lastIndex, iniMatch: m.index });
    if (RE_ROTULO.lastIndex === m.index) RE_ROTULO.lastIndex++; // trava de segurança
  }

  const achado: Record<string, unknown> = {};
  marcas.forEach((marca, i) => {
    const fim = i + 1 < marcas.length ? marcas[i + 1].iniMatch : texto.length;
    const valor = valorDoCampo(marca.campo, texto.slice(marca.iniValor, fim));
    if (valor !== undefined && achado[marca.campo] === undefined) achado[marca.campo] = valor;
  });
  return achado as DadosContrato;
}

/** Abertura de conversa não é valor de campo — sem isto "oi, tudo bem?" virava
 *  o nome do responsável. */
const RE_CONVERSA = /^(oi|ol[áa]|opa|e a[íi]|bom dia|boa tarde|boa noite|tudo bem|ok|certo|sim|n[ãa]o|obrigad|valeu|beleza|isso|pode ser|ent[ãa]o|espera|calma|como assim|quero|manda)\b/i;

/** "é a Dra. Tayse" / "o nome é Dr. Fulano" — tira a abertura e fica com o nome. */
const RE_ABERTURA_NOME = /^(?:(?:[ée]|eh)\s+(?:o|a)\s+|seria\s+(?:o|a\s+)?|vai ser\s+|coloca(?:r)?\s+|p[oõ]e\s+|o nome\s+(?:do projeto\s+)?(?:[ée]\s+)?|nome\s*:\s*|chama(?:-se)?\s+)+/i;

/** Só aceita resposta curta, afirmativa e sem dígito como valor de texto. */
function respostaDeTexto(texto: string, campo: string): string | undefined {
  const t = normalizarTexto(texto);
  if (!t || t.length < 2 || t.length > 80) return undefined;
  if (t.includes("?") || /\d/.test(t)) return undefined;
  if (RE_CONVERSA.test(t)) return undefined;
  const palavras = t.split(/\s+/);
  if (palavras.length > 6) return undefined;
  // Nome pede nome e sobrenome; cidade/UF podem ser uma palavra só.
  if ((campo === "nome_completo" || campo === "nome_empresarial") && palavras.length < 2) return undefined;
  return t;
}

/**
 * @param texto  última mensagem do usuário
 * @param esperado  campo que o sistema pediu no turno anterior — dá o contexto
 *                  para aceitar uma resposta sem rótulo ("379.699.098-33").
 */
export function extrairDadosDaMensagem(texto: string, esperado?: string): DadosContrato {
  const achado: DadosContrato = {};
  if (!texto) return achado;
  const p = achado as Record<string, unknown>;

  // 1) "Rótulo: valor" — cobre a ficha inteira colada de uma vez.
  Object.assign(achado, extrairRotulados(texto));

  // 2) Documento pontuado em qualquer lugar do texto.
  const formatado = acharFormatado(texto) as Record<string, unknown>;
  for (const [k, v] of Object.entries(formatado)) if (p[k] === undefined) p[k] = v;

  if (p.email_assinatura === undefined) {
    const email = texto.match(RE_EMAIL_SOLTO)?.[0];
    if (email) achado.email_assinatura = normalizarEmail(email);
  }
  if (p.data_inicio === undefined) {
    const data = texto.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/)?.[0];
    if (data) achado.data_inicio = data;
  }
  // 3) Jeito real de escrever: "Full Performance 3k mês 3 meses".
  if (p.plano === undefined) {
    const plano = texto.match(/(?:full|light)\s+performance(?:\s*\+\s*social)?/i)?.[0];
    if (plano) achado.plano = normalizarPlano(plano);
  }
  if (p.valor_mensal === undefined) {
    const k = texto.match(/\b\d+(?:[.,]\d+)?\s*(?:k\b|mil\b)/i)?.[0] ?? texto.match(/r\$\s*[\d.,]+/i)?.[0];
    if (k) achado.valor_mensal = valorSolto(k);
  }
  if (p.tempo_contrato === undefined) {
    const meses = texto.match(/\b(\d+)\s*(?:meses|m[êe]s)\b/i)?.[1];
    if (meses && Number(meses) >= 1) achado.tempo_contrato = Number(meses);
  }

  // 3b) Link de transcrição nunca passa por respostaDeTexto (tem dígito e é
  //     longo), então colhe de qualquer lugar do texto.
  if (p.transcricoes === undefined) {
    const links = normalizarTranscricoes(texto);
    if (links.length) achado.transcricoes = links;
  }

  // 4) Resposta solta: só vale para o campo que o sistema acabou de pedir.
  if (!esperado || p[esperado] !== undefined) return achado;

  const digitosEsperados = DIGITOS_POR_CAMPO[esperado];
  if (digitosEsperados) {
    const bloco = blocosDeDigitos(texto).find((b) =>
      b.length === digitosEsperados && (esperado !== "cpf" || cpfTemDvValido(b))
    );
    if (bloco) p[esperado] = bloco;
    return achado;
  }

  if (esperado === "valor_mensal") {
    const valor = valorSolto(texto);
    if (valor) achado.valor_mensal = valor;
  } else if (esperado === "dia_pagamento") {
    const n = Number(texto.match(/\d+/)?.[0] ?? "");
    if (n >= 1 && n <= 31) achado.dia_pagamento = n;
  } else if (esperado === "tempo_contrato") {
    const n = Number(texto.match(/\d+/)?.[0] ?? "");
    if (n >= 1) achado.tempo_contrato = n;
  } else if (esperado === "plano") {
    const plano = normalizarPlano(texto);
    if (plano) achado.plano = plano;
  } else if (esperado === "nome_projeto") {
    const t = normalizarTexto(texto).replace(RE_ABERTURA_NOME, "").trim();
    // Documento respondido no lugar do nome do projeto vai para o campo certo —
    // e o nome do projeto SEGUE faltando, entao o sistema pergunta de novo em vez
    // de batizar o grupo de WhatsApp com um CNPJ.
    const dig = soDigitos(t);
    if (/^[\d\s.\-/]+$/.test(t)) {
      if (dig.length === 14 && p.cnpj === undefined) p.cnpj = dig;
      else if (dig.length === 11 && cpfTemDvValido(dig) && p.cpf === undefined) p.cpf = dig;
    }
    if (!t.includes("?") && !RE_CONVERSA.test(t) && nomeProjetoValido(t)) achado.nome_projeto = t;
  } else if (["nome_completo", "nome_empresarial", "endereco", "cidade", "estado"].includes(esperado)) {
    const t = respostaDeTexto(texto, esperado);
    if (t) p[esperado] = esperado === "estado" && t.length === 2 ? t.toUpperCase() : t;
  }

  return achado;
}

// ----------------------------------------------------------------------------
// Higiene do que o MODELO devolve. O LLM às vezes escreve frase explicativa
// dentro de um campo de dado ("volume_posts": "Não se aplica, plano Full...")
// ou inventa campo. Aqui só passa dado curto, de campo conhecido.
// ----------------------------------------------------------------------------

const LIMITE_PADRAO = 200;
const LIMITE_CURTO = 40;
const CAMPOS_CURTOS = new Set([
  "cpf", "cnpj", "cep", "telefone", "rg", "numero", "estado", "dia_pagamento",
  "volume_posts", "data_inicio", "tempo_contrato", "valor_mensal", "estado_civil",
]);
const CAMPOS_VALIDOS = new Set([
  "nome_empresarial", "cnpj", "endereco", "numero", "bairro", "cidade", "estado",
  "complemento", "cep", "nome_completo", "profissao", "estado_civil", "nacionalidade",
  "rg", "cpf", "email_assinatura", "dia_pagamento", "plano", "valor_mensal",
  "volume_posts", "telefone", "tempo_contrato", "data_inicio", "modelo_contrato",
  "nome_projeto", "transcricoes",
]);

export function sanitizarDadosDoModelo(bruto: unknown): DadosContrato {
  const saida: Record<string, unknown> = {};
  if (!bruto || typeof bruto !== "object") return saida as DadosContrato;
  for (const [chave, valor] of Object.entries(bruto as Record<string, unknown>)) {
    if (!CAMPOS_VALIDOS.has(chave) || valor == null) continue;
    if (typeof valor === "number" || typeof valor === "boolean") { saida[chave] = valor; continue; }
    if (chave === "transcricoes") {
      const links = normalizarTranscricoes(valor);
      if (links.length) saida[chave] = links;
      continue;
    }
    if (typeof valor !== "string") continue; // objeto/array vindo do modelo: descarta
    const s = valor.trim();
    if (!s) continue;
    // Campo curto que veio como frase é lixo do modelo — descarta e deixa faltando,
    // para a verificação determinística pedir de novo.
    if (CAMPOS_CURTOS.has(chave) && s.length > LIMITE_CURTO) continue;
    // O modelo as vezes ecoa o documento como nome do projeto. Esse campo nomeia
    // ClickUp, Discord, grupo de WhatsApp e Drive: so passa se parecer nome.
    if (chave === "nome_projeto" && !nomeProjetoValido(s)) continue;
    saida[chave] = s.length > LIMITE_PADRAO ? s.slice(0, LIMITE_PADRAO) : s;
  }
  return saida as DadosContrato;
}

/**
 * Lista, na ordem natural de conversa, TUDO que ainda falta — mesmas regras de
 * validarDados. É isto que vai no prompt: o modelo não decide mais o que falta
 * nem valida formato (ele conta dígitos errado e trava o chat num loop).
 */
export function camposFaltantes(input: DadosContrato): { campo: string; label: string }[] {
  const d = normalizarDados(input);
  const modelo = detectarModelo(d);
  const faltando: { campo: string; label: string }[] = [];
  const falta = (campo: string, label: string) => faltando.push({ campo, label });

  if (!nomeProjetoValido(d.nome_projeto)) falta("nome_projeto", "Nome oficial do projeto (nome de pessoa ou marca, nunca CNPJ, CPF ou número)");
  if (modelo === "PJ" && !d.nome_empresarial) falta("nome_empresarial", "Razão social (nome empresarial)");
  if (modelo === "PJ" && soDigitos(d.cnpj).length !== 14) falta("cnpj", "CNPJ");
  if (!d.nome_completo) falta("nome_completo", "Nome do responsável");
  if (soDigitos(d.cpf).length !== 11) falta("cpf", "CPF do responsável");
  if (!d.email_assinatura || !EMAIL_RE.test(d.email_assinatura)) falta("email_assinatura", "E-mail de assinatura");
  if (!d.plano) falta("plano", "Plano");
  if (d.valor_mensal == null || Number(d.valor_mensal) <= 0) falta("valor_mensal", "Valor mensal");
  const dia = Number(String(d.dia_pagamento ?? "").match(/\d+/)?.[0] ?? "");
  if (!dia || dia < 1 || dia > 31) falta("dia_pagamento", "Melhor dia de pagamento (1-31)");
  const tempo = Number(String(d.tempo_contrato ?? "").match(/\d+/)?.[0] ?? "");
  if (!tempo || tempo < 1) falta("tempo_contrato", "Tempo de contrato (meses)");
  if (!parseDataInicio(d.data_inicio)) falta("data_inicio", "Data de início (DD/MM/AAAA)");
  if (!d.endereco) falta("endereco", "Endereço (logradouro)");
  if (!d.cidade) falta("cidade", "Cidade");
  if (!d.estado) falta("estado", "Estado (UF)");
  if (!soDigitos(d.cep)) falta("cep", "CEP");
  if (!d.transcricoes || d.transcricoes.length === 0) falta("transcricoes", "Link da transcrição da reunião de fechamento");
  return faltando;
}
